import { Switch } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import CustomTimeSelector from './CustomTimeSelector'
import TimezonePicker from '../TimezonePicker'
import styles from './EventEditor.module.scss'

import calendarIcon from 'assets/images/calendar/icon-calendar-24.svg'
import clockIcon from 'assets/images/calendar/property-schedule.svg'
import timezoneIcon from 'assets/images/calendar/property-timezone.svg'

import moment from 'moment'
import 'moment-timezone'
import settings from 'popups/Settings'

import { format12Hours } from './timeFormats'

interface EditEventData {
  startDate: Date
  endDate: Date
  startTz: string
  endTz: string
  isAllDay: boolean
  isMyEvent: boolean
}

interface TimeSelectorProps {
  editEventData: EditEventData
  startDateOpen: boolean
  endDateOpen: boolean
  showTimezonePicker: boolean
  onStartDateChange: (date: Dayjs) => void
  onEndDateChange: (date: Dayjs) => void
  onStartTimeChange: (time: string) => void
  onEndTimeChange: (time: string) => void
  onAllDayChange: (checked: boolean) => void
  onTimezoneClick: () => void
  setStartDateOpen: (open: boolean) => void
  setEndDateOpen: (open: boolean) => void
  onStartTzChange: (tz: string) => void
  onEndTzChange: (tz: string) => void
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
  editEventData,
  startDateOpen,
  endDateOpen,
  showTimezonePicker,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onAllDayChange,
  onTimezoneClick,
  setStartDateOpen,
  setEndDateOpen,
  onStartTzChange,
  onEndTzChange,
}) => {
  const { t } = useTranslation()

  const getTZOffsetFromMoment = (momentDate: moment.Moment): string => {
    const offset = momentDate.format('Z')
    const isDST = momentDate.isDST()
    return `UTC${offset}${isDST ? '☀️' : ''}`
  }

  const getTimezoneOffset = (tz: string | undefined, date: Date): string => {
    if (!tz) {
      tz = 'Etc/UTC'
    }
    const momentDate = moment(date).tz(tz)
    return getTZOffsetFromMoment(momentDate)
  }

  const startTimeSelectorProps = {
    timing: 'start' as const,
    onTimeChange: onStartTimeChange,
    editEventData: editEventData,
    defaultValue: format12Hours(editEventData.startDate, editEventData.startTz),
  }

  const endTimeSelectorProps = {
    timing: 'end' as const,
    onTimeChange: onEndTimeChange,
    editEventData: editEventData,
    defaultValue: format12Hours(editEventData.endDate, editEventData.endTz),
  }

  const dateFormat = settings.getValue('general').dateFormat as string

  return (
    <div className={styles.timeSelectWrapper}>
      <img src={clockIcon} alt="Time" className={styles.titleIcon} />
      <div className={`${styles.timeInputs} ${!editEventData.isMyEvent ? styles.dimmed : ''}`}>
        <div className={styles.timeRow}>
          <div className={styles.datePickerContainer}>
            {/* AntDesign DatePicker 를 제거 */}
            <button
              className={styles.dateButton}
              onClick={() => {
                setStartDateOpen(!startDateOpen)
                if (!startDateOpen) setEndDateOpen(false)
              }}
            >
              <span className={styles.dateValue}>
                {dayjs(editEventData.startDate).tz(editEventData.startTz).format(dateFormat)}
              </span>
              <img src={calendarIcon} alt="Calendar" className={styles.chevronIcon} />
            </button>
            {startDateOpen && (
              <div className={styles.datePickerOverlay} onClick={() => setStartDateOpen(false)}>
                <div onClick={(e) => e.stopPropagation()}>
                  <CalendarDatePicker
                    value={dayjs(editEventData.startDate).tz(editEventData.startTz).toDate()}
                    onChange={(date) => {
                      onStartDateChange(dayjs(date))
                      setStartDateOpen(false)
                    }}
                    onClose={() => setStartDateOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
          {!editEventData.isAllDay && (
            <React.Fragment>
              <CustomTimeSelector {...startTimeSelectorProps} />
              {editEventData.startTz === editEventData.endTz && !showTimezonePicker ? (
                <button className={styles.timezoneButton} onClick={onTimezoneClick}>
                  <img src={timezoneIcon} alt="Timezone" />
                  <span>{getTimezoneOffset(editEventData.startTz, editEventData.startDate)}</span>
                </button>
              ) : (
                <TimezonePicker
                  tzId={editEventData.startTz}
                  focusDate={editEventData.startDate}
                  onTzChange={onStartTzChange}
                />
              )}
            </React.Fragment>
          )}
          {!showTimezonePicker && (
            <div className={styles.allDaySwitch}>
              <Switch size="small" checked={editEventData.isAllDay} onChange={onAllDayChange} />
              <span>{t('calendar.edit.allDay')}</span>
            </div>
          )}
          {showTimezonePicker && (
            <button className={styles.timezoneButton} onClick={onTimezoneClick}>
              <img src={timezoneIcon} alt="Timezone" />
            </button>
          )}
        </div>
        <div className={styles.timeRow}>
          <div className={styles.datePickerContainer}>
            <button
              className={styles.dateButton}
              onClick={() => {
                setEndDateOpen(!endDateOpen)
                if (!endDateOpen) setStartDateOpen(false)
              }}
            >
              <span className={styles.dateValue}>
                {dayjs(editEventData.endDate).tz(editEventData.endTz).format(dateFormat)}
              </span>
              <img src={calendarIcon} alt="Calendar" className={styles.chevronIcon} />
            </button>
            {endDateOpen && (
              <div className={styles.datePickerOverlay} onClick={() => setEndDateOpen(false)}>
                <div onClick={(e) => e.stopPropagation()}>
                  <CalendarDatePicker
                    value={dayjs(editEventData.endDate).tz(editEventData.endTz).toDate()}
                    onChange={(date) => {
                      onEndDateChange(dayjs(date))
                      setEndDateOpen(false)
                    }}
                    onClose={() => setEndDateOpen(false)}
                    limitDate={{
                      startDate: new Date(new Date(editEventData.startDate).setHours(0, 0, 0, 0)),
                      endDate: null,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {!editEventData.isAllDay && <CustomTimeSelector {...endTimeSelectorProps} />}
          {showTimezonePicker && (
            <TimezonePicker
              tzId={editEventData.endTz}
              focusDate={editEventData.endDate}
              onTzChange={onEndTzChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default TimeSelector
